function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import mean from 'lodash/mean';
import zip from 'lodash/zip';
import { jsx } from 'panel';
import { LENGTH, SIZE, TEXT_HIGHLIGHT_THEME, TextHighlightEl } from 'common/components/text-highlight';
import { Placement } from 'common/components/tooltip/types';
import { renamePropertyValue } from 'common/query-builder/cohorts-query-builder/util';
import { LIFT_TOOLTIP_COMPARE, LiftType } from 'common/report/util/lift/types';
import { formatMetricNumber, formatPercent } from 'common/util';
import { formatDate, UNITS } from 'common/util/date';
import { TimeUnit } from 'common/widgets/date-range-picker/models/types';
import { TooltipEl } from 'common/components/tooltip';
import { GroupClause } from 'common/report/insights/util/types';
import { DEFAULT_LIFT_COLUMN_WIDTH } from '../constants';
import { CellRendererFunc, ColumnGroup, CommonDataTableFields, SegmentColumnSpec, TableColumn, TableColumnType, TableDataType, TableRow } from '../types';
import { TrendDataRow, ValueFormatterFunc } from './types';
import { GroupSortableTrendColumns } from './constants';
export function getTrendDataTableRow(row: TrendDataRow, liftComparisonValueGenerator?: (timestamp: number) => number): TableRow {
  const hasTimeComparison = Boolean(row.timeComparisonData);
  const hasLiftComparison = Boolean(row.liftComparisonData);
  // Don't show time comparison when we're in lift mode
  const showTimeComparison = hasTimeComparison && !hasLiftComparison;
  let liftComparisons = [];
  if (hasLiftComparison) {
    liftComparisons = row.liftComparisonData.map(_ref => {
      let {
        highchartsTimestamp,
        value
      } = _ref;
      return ["".concat(CommonDataTableFields.LiftComparisonDatePointPrefix).concat(highchartsTimestamp), value];
    });
    if (liftComparisonValueGenerator) {
      liftComparisons = liftComparisons.concat(row.data.map(_ref2 => {
        let {
          highchartsTimestamp
        } = _ref2;
        return ["".concat(CommonDataTableFields.LiftComparisonExtraFieldPrefix).concat(highchartsTimestamp), liftComparisonValueGenerator(highchartsTimestamp) || 0];
      }));
    }
  }
  const allDateValueMap = Object.fromEntries([...row.data.map(_ref3 => {
    let {
      highchartsTimestamp,
      value
    } = _ref3;
    return ["".concat(CommonDataTableFields.DatePointPrefix).concat(highchartsTimestamp), value];
  }), ...(showTimeComparison ? row.timeComparisonData.map(_ref4 => {
    let {
      highchartsTimestamp,
      value
    } = _ref4;
    return ["".concat(CommonDataTableFields.TimeComparisonDatePointPrefix).concat(highchartsTimestamp), value];
  }) : []), ...liftComparisons]);
  const segmentsMap = Object.fromEntries(row.segmentValues.map((segmentValue, i) => ["".concat(CommonDataTableFields.SegmentPrefix).concat(i), segmentValue]));
  return _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
    identifier: row.rowId,
    checkedColor: row.selectionColor,
    isSelected: row.isSelected
  }, segmentsMap), Number.isFinite(row.cohortSize) ? {
    [CommonDataTableFields.CohortSize]: row.cohortSize
  } : {}), {}, {
    [CommonDataTableFields.Average]: mean(row.data.map(_ref5 => {
      let {
        value
      } = _ref5;
      return value;
    }))
  }, showTimeComparison ? {
    [CommonDataTableFields.TimeComparisonAverage]: mean(row.timeComparisonData.map(_ref6 => {
      let {
        value
      } = _ref6;
      return value;
    }))
  } : {}), hasLiftComparison ? {
    [CommonDataTableFields.LiftAverage]: mean(row.liftComparisonData.map(point => point.value))
  } : {}), allDateValueMap);
}
const HEADER_CUSTOM_DATE_FORMATTING = {
  [UNITS.minute]: "h:mma",
  [UNITS.hour]: "MMM D, ha",
  [UNITS.day]: "MMM D",
  [UNITS.week]: "MMM D",
  [UNITS.month]: "MMM YYYY",
  [UNITS.quarter]: "[Q]Q YYYY"
};

// TODO: Add unit tests for this
export function getTrendDataTableColumnGroups(_ref7: {
  groupClauses: GroupClause[];
  liftType?: LiftType;
  row: TrendDataRow;
  segments: SegmentColumnSpec[];
  timezone: string;
  trendUnit: TimeUnit;
  valueFormatter: ValueFormatterFunc;
}): ColumnGroup[] {
  let {
    groupClauses,
    liftType = null,
    row,
    // only a single row is required to generate columns
    segments,
    timezone,
    trendUnit,
    valueFormatter
  }: {
    groupClauses: GroupClause[];
    liftType?: LiftType;
    row: TrendDataRow;
    segments: SegmentColumnSpec[];
    timezone: string;
    trendUnit: TimeUnit;
    valueFormatter: ValueFormatterFunc;
  } = _ref7;
  const hasTimeComparison = Boolean(row.timeComparisonData);
  const hasLiftData = Boolean(row.liftComparisonData);
  // Don't show time comparison when we're in lift mode
  const showTimeComparison = hasTimeComparison && !hasLiftData;
  const CommonSegmentColumnFields = {
    isSortable: true,
    sticky: true,
    type: TableColumnType.Segment as const
  };
  const segmentColumnGroups = segments.map((segment, i) => {
    const groupClause = groupClauses[i - 1];
    const column = _objectSpread(_objectSpread({}, CommonSegmentColumnFields), {}, {
      title: segment.title,
      field: "".concat(CommonDataTableFields.SegmentPrefix).concat(i),
      hasOrdinalPrefix: i === 0,
      contentType: segment.segmentType,
      labelTransform: (trendRow, field) =>
      // We want the property id not the name. segment.propertyName is the
      // name but not the id of the property, so try the groupClause.value first.
      renamePropertyValue(trendRow[field], groupClause ? groupClause.value : segment.propertyName, groupClause)
    });
    return {
      columns: [column]
    };
  });
  let averageColumnCellRenderer: CellRendererFunc<any> = null;
  if (liftType === LiftType.Overall) {
    averageColumnCellRenderer = _ref8 => {
      let {
        item,
        context
      } = _ref8;
      return formatPercent(item[context.field]);
    };
  } else {
    averageColumnCellRenderer = _ref9 => {
      let {
        item,
        context
      } = _ref9;
      return valueFormatter(item, context, {
        isAverage: true
      });
    };
  }
  const CommonAverageColumnFields = _objectSpread({
    isSortable: true,
    sticky: true,
    type: TableColumnType.Data as const,
    contentType: TableDataType.Number
  }, averageColumnCellRenderer ? {
    cellRenderer: averageColumnCellRenderer
  } : {});
  const averageField = liftType === LiftType.Overall ? CommonDataTableFields.LiftAverage : CommonDataTableFields.Average;
  const averageColumns: TableColumn[] = [_objectSpread(_objectSpread({}, CommonAverageColumnFields), {}, {
    title: "Average",
    field: averageField
  }, averageField in GroupSortableTrendColumns ? GroupSortableTrendColumns[averageField] : {}), ...(showTimeComparison ? [_objectSpread(_objectSpread({}, CommonAverageColumnFields), {}, {
    title: "Past Avg.",
    field: CommonDataTableFields.TimeComparisonAverage
  })] : [])];

  // Trim data and time comparison data to ensure both have equal number of date points, because
  // timezone offset changes (usually DST) can cause either of them to have fewer date points than the other.
  const pointCountDiff = hasTimeComparison ? row.timeComparisonData.length - row.data.length : 0;
  const equalLengthData = pointCountDiff === 0 ? row.data : row.data.slice(0, row.data.length + pointCountDiff);
  const equalLengthTimeComparisonData = pointCountDiff === 0 ? row.timeComparisonData : row.timeComparisonData.slice(0, row.timeComparisonData.length - pointCountDiff);
  const allDatePoints = zip(equalLengthData, equalLengthTimeComparisonData);
  const CommonDateColumnFields = {
    isSortable: true,
    type: TableColumnType.Data as const,
    contentType: TableDataType.Number,
    cellRenderer: _ref10 => {
      let {
        item,
        context
      } = _ref10;
      return valueFormatter(item, context);
    }
  };
  const datePointColumnGroups: ColumnGroup[] = allDatePoints.map((_ref11): ColumnGroup => {
    let [datePoint, timeComparisonDatePoint] = _ref11;
    const columns: TableColumn[] = [_objectSpread(_objectSpread(_objectSpread({}, CommonDateColumnFields), {}, {
      title: formatDate(datePoint.highchartsTimestamp, {
        unit: trendUnit,
        customFormatting: HEADER_CUSTOM_DATE_FORMATTING,
        displayRangeIfWeek: false
      }),
      tooltip: formatDate(datePoint.timestamp, {
        unit: trendUnit,
        timezone
      }),
      field: "".concat(CommonDataTableFields.DatePointPrefix).concat(datePoint.highchartsTimestamp)
    }, hasLiftData ? {
      liftField: "".concat(CommonDataTableFields.LiftComparisonDatePointPrefix).concat(datePoint.highchartsTimestamp),
      liftExtra: "".concat(CommonDataTableFields.LiftComparisonExtraFieldPrefix).concat(datePoint.highchartsTimestamp),
      sortTransform: trendRow => {
        return trendRow["".concat(CommonDataTableFields.LiftComparisonDatePointPrefix).concat(datePoint.highchartsTimestamp)];
      },
      // TODO(reviewer) - this is really the only safe way to do this before the new visuals come out?
      // set width of data table cells to be slightly wider to allow for the extra lift pill
      initialWidth: DEFAULT_LIFT_COLUMN_WIDTH
    } : {}), {}, {
      cellRenderer: getDataPointCellRenderer(valueFormatter, liftType, hasLiftData)
    })];
    if (showTimeComparison) {
      columns.push(_objectSpread(_objectSpread({}, CommonDateColumnFields), {}, {
        title: "".concat(formatDate(timeComparisonDatePoint.highchartsTimestamp, {
          unit: trendUnit,
          customFormatting: HEADER_CUSTOM_DATE_FORMATTING,
          displayRangeIfWeek: false
        }), " (Past)"),
        tooltip: "".concat(formatDate(timeComparisonDatePoint.timestamp, {
          unit: trendUnit,
          timezone
        }), " (Past)"),
        field: "".concat(CommonDataTableFields.TimeComparisonDatePointPrefix).concat(timeComparisonDatePoint.highchartsTimestamp)
      }));
    }
    return {
      columns
    };
  });
  return [...segmentColumnGroups, {
    columns: averageColumns
  }, ...datePointColumnGroups];
}
function getDataPointCellRenderer(valueFormatter: ValueFormatterFunc, liftType, hasLiftData): CellRendererFunc<any> {
  return _ref12 => {
    let {
      item,
      context
    } = _ref12;
    if (hasLiftData) {
      if (liftType === LiftType.Overall) {
        return <>
            <div sel='.mp-table-cell'>
              {formatPercent(item[context.liftField])}
              <TooltipEl attrs={{
              placement: Placement.Top,
              text: "".concat(valueFormatter(item, context), " ").concat(LIFT_TOOLTIP_COMPARE, " ").concat(valueFormatter(item, context, {
                isLift: true
              }))
            }} />
            </div>
          </>;
      }

      // Baseline and compare to past share the same logic
      const isPositive = item[context.liftField] > 0;
      // Have to fix it to two decimals and re-parse because of JS floating point number multiplication (otherwise, 2.99 * 100 becomes 299.99, etc)
      const percent = Number((100 * item[context.liftField]).toFixed(2));
      return <>
          <div sel='.mp-table-cell'>
            {valueFormatter(item, context)}
            <TextHighlightEl sel='.mp-lift-pill' attrs={{
            text: "".concat(isPositive ? "+" : "").concat(formatMetricNumber(percent), "%"),
            theme: isPositive ? TEXT_HIGHLIGHT_THEME.POSITIVE : TEXT_HIGHLIGHT_THEME.NEGATIVE,
            size: SIZE.SMALL,
            length: LENGTH.THIN
          }} />
            <TooltipEl attrs={{
            placement: Placement.Top,
            text: "".concat(valueFormatter(item, context), " ").concat(LIFT_TOOLTIP_COMPARE, " ").concat(valueFormatter(item, context, {
              isLift: true
            }))
          }} />
          </div>
        </>;
    }
    return valueFormatter(item, context);
  };
}